import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { listAppSettings, updateAppSetting } from 'src/utils/appSettingsApi';
import GameweekTransitionCard from 'src/sections/app-settings/GameweekTransitionCard';

const formatUpdatedAt = value => {
  if (!value) return 'Not updated yet';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Unknown' : date.toLocaleString();
};

const UNGROUPED_SECTION = 'Other';

/**
 * Groups the settings under their `section` heading, keeping the order the API
 * returns them in - both for the sections themselves and within each one. A
 * setting from an older backend that has no section falls into "Other" rather
 * than disappearing.
 */
const groupBySection = settings => {
  const sections = new Map();

  settings.forEach(setting => {
    const title = setting.section || UNGROUPED_SECTION;
    const existing = sections.get(title);
    if (existing) {
      existing.push(setting);
      return;
    }
    sections.set(title, [setting]);
  });

  return Array.from(sections, ([title, items]) => ({ title, items }));
};

export default function AppSettingsPage() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);
  const sections = useMemo(() => groupBySection(settings), [settings]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSettings(await listAppSettings());
    } catch {
      setError('Unable to load application settings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleToggle = async (setting, enabled) => {
    setSavingKey(setting.key);
    setError(null);
    setNotice(null);
    try {
      const updated = await updateAppSetting(setting.key, enabled);
      setSettings(current =>
        current.map(item => (item.key === setting.key ? { ...item, ...updated } : item))
      );
      setNotice(`${setting.label} ${enabled ? 'enabled' : 'disabled'}.`);
    } catch {
      setError(`Unable to update ${setting.label}.`);
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack spacing={1} sx={{ mb: 3 }}>
        <Typography variant="h4">App Settings</Typography>
        <Typography color="text.secondary">
          Runtime controls for fantasy team actions and what players are shown. Backend enforcement
          remains authoritative even while a player has an older page open.
        </Typography>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {notice ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          {notice}
        </Alert>
      ) : null}

      <Box sx={{ mb: 4 }}>
        <GameweekTransitionCard />
      </Box>

      {loading ? (
        <Stack alignItems="center" sx={{ py: 8 }}>
          <CircularProgress />
        </Stack>
      ) : (
        <Stack spacing={4}>
          {sections.map(section => (
            <Stack key={section.title} spacing={2}>
              <Typography variant="h6">{section.title}</Typography>
              {section.items.map(setting => {
                const isSaving = savingKey === setting.key;
                return (
                  <Card key={setting.key} variant="outlined">
                    <CardContent>
                      <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                        justifyContent="space-between"
                      >
                        <Stack spacing={0.75}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle1">{setting.label}</Typography>
                            <Chip
                              size="small"
                              color={setting.is_enabled ? 'success' : 'default'}
                              label={setting.is_enabled ? 'Enabled' : 'Disabled'}
                            />
                          </Stack>
                          <Typography color="text.secondary">{setting.description}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Last updated: {formatUpdatedAt(setting.updated_at)}
                          </Typography>
                        </Stack>
                        <FormControlLabel
                          label={isSaving ? 'Saving…' : setting.is_enabled ? 'Enabled' : 'Disabled'}
                          control={
                            <Switch
                              checked={setting.is_enabled}
                              disabled={savingKey !== null}
                              onChange={event => void handleToggle(setting, event.target.checked)}
                            />
                          }
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          ))}
        </Stack>
      )}
    </Box>
  );
}
